<template>
  <div
    class="fundraiser-card"
    data-test="fundraiser-card"
  >
    <div
      v-if="donorStore.isLoading"
      class="loading-overlay"
      data-test="loading-overlay"
    ></div>
    <div
      class="fundraiser-image"
      data-test="fundraiser-image"
    >
      <img
        :src="fundraiser.heroLink"
        alt="Hero Image"
      />
    </div>
    <div
      class="action-status"
      data-test="action-status"
    >
      <Tag
        rounded
        class="status-badge"
        data-test="status-badge"
        :severity="mappedStatus"
        :value="fundraiser.status"
      ></Tag>
      <Button
        type="button"
        icon="pi pi-ellipsis-h"
        class="p-button-secondary p-button-text p-button-sm action-button"
        aria-haspopup="true"
        aria-controls="overlay_menu"
        style="width: 10px"
        :data-test="'action-button-' + fundraiser.fundraisers_id"
        @click="toggle"
      />
      <Menu
        ref="menu"
        id="overlay_menu"
        :model="menuItems"
        :popup="true"
      />
    </div>
    <div
      class="bottom-fundraiser-card"
      data-test="bottom-fundraiser-card"
    >
      <div
        class="fundraiser-name"
        data-test="fundraiser-name"
      >
        {{ fundraiser.title }}
      </div>
      <div
        class="org-name"
        data-test="org-name"
      >
        {{ fundraiser.associatedOrgName }}
      </div>
      <div
        class="progress-bar"
        data-test="progress-bar"
      >
        <div
          :style="{
            width: calculateWidth(fundraiser),
            background: donorStore.mainColor,
            height: '100%',
            borderRadius: 'inherit',
          }"
        ></div>
      </div>
      <div
        class="amount-raised"
        data-test="amount-raised"
      >
        {{ getAmount(fundraiser) }} / {{ getGoal(fundraiser) }}
      </div>
      <Button
        v-if="showDetailsButton"
        class="p-button-m"
        :style="{ width: '100%', justifyContent: 'center', color: getContrastYIQ(donorStore.mainColor) }"
        @click="goToDetailPage(fundraiser.fundraisers_id)"
        :data-test="'view-details-button-' + fundraiser.fundraisers_id"
      >
        <span>View Details</span>
      </Button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onBeforeMount } from 'vue';
import filters from '@/helpers/filters';
import getContrastYIQ from '@/helpers/getContrastYIQ';
import { useDonorStore } from '@/stores/DonorStore.js';
import { useRouter } from 'vue-router';
import PencilBlack from '@/assets/PencilBlack.svg?raw';
import Eye from '@/assets/Eye.svg?raw';
import Deactivate from '@/assets/Deactivate.svg?raw';
import Copy from '@/assets/Copy.vue';
import { useSnackbarStore } from '@/stores/SnackbarStore.js';
import { useFundraiserStore } from '@/stores/FundraiserStore.js';


const donorStore = useDonorStore();
const fundraiserStore = useFundraiserStore();
const snackbarStore = useSnackbarStore();

const router = useRouter();

const props = defineProps({
  fundraiser: Object,
  showDetailsButton: {
    type: Boolean,
    default: true,
  },
});

const fullUrl = computed(() => `${donorStore.domain.replace(/\/$/, '')}/fundraiser/${props.fundraiser.urlLink}`);
const menu = ref();

// view fundraiser page
const openFundraiserPage = () => {
  window.open(props.fundraiser.domain.replace(/\/$/, '') + '/fundraiser/' + props.fundraiser.urlLink, '_blank');
};

const baseMenuItems = [
  {
    text: 'Visit Page',
    emit: 'visitPage',
    disabled: false,
    disabled_text: null,
    type: 'default',
    icon: Eye,
  },
];

const goToDetailPage = (fundraiserId) => {
  router.push(`fundraisers/${fundraiserId}`);
};

const goToEditPage = (fundraiserId) => {
  router.push(`fundraisers/${fundraiserId}/edit`);
};

const dynamicMenuItems = computed(() => {
  const isOwner = 
  props.fundraiser.donorAccounts_id === donorStore.donorAccounts_id || 
  props.fundraiser.donorAccounts_id == null; // This applies to donor account fundraisers 
  const status = props.fundraiser.status;
  const items = [...baseMenuItems];
  if (isOwner) {
    items.push({
      text: 'Edit Page',
      emit: 'editPage',
      disabled: false,
      disabled_text: null,
      type: 'default',
      icon: PencilBlack,
    });
  } else {
    items.push({
      text: 'Copy Link',
      emit: 'copyLink',
      icon: Copy,
      disabled: false,
      disabled_text: null,
      type: 'default',
    });
  }
  if (status === 'Published') {
    items.push({
      text: 'Deactivate',
      emit: 'unPublishPage',
      disabled: false,
      disabled_text: null,
      type: 'danger',
      icon: Deactivate,
    });
  }
  return items;
});

const menuStr = computed(() => JSON.stringify(dynamicMenuItems.value));
// const menuStr = JSON.stringify(menuItems);

const amount = ref(0);
const goal = ref(0);

const getAmount = (fundraiser, raw = false) => {
  if (fundraiser.status == 'published') {
    const data = donorStore.fundraiserDonationData[fundraiser.fundraisers_id];
    if (!data) {
      return raw ? 0 : filters.formatMoney(0, 0);
    }
    amount.value = data.amount;
    return raw ? data.amount : filters.formatMoney(data.amount, 0);
  } else {
    amount.value = fundraiser.amountRaised_cached;
    return filters.formatMoney(fundraiser.amountRaised_cached, 0);
  }
};

const getGoal = (fundraiser, cachedGoal, raw = false) => {
  if (fundraiser.status == 'published') {
    const data = donorStore.fundraiserDonationData[fundraiser.fundraisers_id];
    if (!data) {
      return raw ? cachedGoal : filters.formatMoney(cachedGoal, 0);
    }
    goal.value = data.goal;
    return raw ? data.goal : filters.formatMoney(data.goal, 0);
  } else {
    goal.value = fundraiser.goal;
    return filters.formatMoney(fundraiser.goal, 0);
  }
};

const copyDefaultValue = () => {
  navigator.clipboard.writeText(fullUrl.value);
  snackbarStore.show('Link Copied!', 'info');
};

// Method to calculate progress bar width
const calculateWidth = () => {
  const percentComplete = Math.min(amount.value / goal.value, 1); // Ensure it does not exceed 100%
  return `${percentComplete * 100}%`;
};
const menuItems = computed(() => {
  return JSON.parse(menuStr.value).map((action) => ({
    label: action.text,
    icon: action.icon,
    disabled: action.disabled,
    command: () => handleAction(action.emit),
  }));
});

const statusMapping = {
  Created: 'warn',
  Submitted: 'warn',
  Declined: 'danger',
  Deactivated: 'secondary',
  Published: 'success',
  Expired: 'secondary',
};
const mappedStatus = computed(() => {
  if (!props.fundraiser) {
    return 'unknown';
  }
  return statusMapping[props.fundraiser.status] || 'unknown';
});

const handleAction = (emitType) => {
  switch (emitType) {
    case 'visitPage':
      openFundraiserPage();
      break;
    case 'editPage':
      goToEditPage(props.fundraiser.fundraisers_id);
      break;
    case 'publishPage':
      donorStore.toggleFundraiserStatus(props.fundraiser.fundraisers_id, 'publish');
      break;
    case 'unPublishPage':
      donorStore.toggleFundraiserStatus(props.fundraiser.fundraisers_id, 'unpublish');
      break;
    case 'copyLink':
      copyDefaultValue();
      break;
    default:
      console.warn(`Unknown action: ${emitType}`);
  }
};

const toggle = (event) => {
  menu.value.toggle(event);
};
onBeforeMount(() => {
  if (props.fundraiser && props.fundraiser.givesDays_id) {
    donorStore.getFundraiserDonationData(props.fundraiser.givesDays_id);
  }
});
</script>

<style>
.actions-icon {
  color: #000;
  font-family: 'Font Awesome 5 Pro';
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px; /* 120% */
}
.header-container {
  display: flex;
  align-items: center; /* This will vertically center the items in the container */
  justify-content: space-between; /* This will place the header on the left and the button on the right */
}
.options-bar {
  flex-wrap: wrap;
  margin-bottom: 20px;
}
.filter {
  margin: 0 10px;
  z-index: 1;
}
.search {
  width: 288px;
  height: 44px;
  flex-shrink: 0;
}
.search-filters {
  align-items: center;
}
.fundraiser-card {
  fill: var(--White, #fff);
  filter: drop-shadow(0px 3px 20px rgba(0, 0, 0, 0.07));
  width: 288px;
  min-height: 288px;
  background-color: var(--White, #fff);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  align-items: center;
  min-width: 250px;
  max-width: 300px;
}
.fundraiser-card.is-loading {
  position: relative;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.7); /* Semi-transparent white overlay */
  display: flex;
  justify-content: center;
  align-items: center;
}
.fundraiser-image {
  width: 100%;
  height: auto;
  height: 144px;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  overflow: hidden;
  border-radius: 8px 8px 0 0; /* Apply rounded corners to top-left and top-right */
}
.fundraiser-image img {
  width: 100%; /* Ensure image takes full width */
  height: 100%; /* Ensure image takes full height */
  object-fit: cover; /* Cover the container without being clipped */
}
.fundraiser-name {
  width: 219px;
  color: #000;
  font-family: Poppins;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 140%;
  margin-top: 9px;
}
.org-name {
  width: 237px;
  color: #000;
  font-family: Poppins;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
  margin-bottom: 12px;
}
.fundraiser-name,
.org-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.status-badge {
  width: 135px;
  height: 20px;
  flex-shrink: 0;
  margin-top: 12px;
  font-weight: 500;
}
.action-status {
  display: flex;
  justify-content: space-between;
  align-items: center; /* Optional: aligns items vertically in the center */
  width: 100%; /* Ensures the div spans the full width of its parent */
  padding: 0 16px 16px;
}
.bottom-fundraiser-card {
  padding: 0 16px 16px;
}
.progress-bar {
  height: 8px;
  flex-shrink: 0;
  border-radius: 4px;
  background: var(--Purple-50, #e6e9ee);
}
.amount-raised {
  color: #000;
  font-family: Poppins;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 25px;
  margin-top: 8px;
  margin-bottom: 16px;
}
.action-picker {
  min-width: 130px;
  max-width: 100%;
  word-wrap: break-word;
}

.p-menuitem {
  padding: 0.5rem 1rem;
}

.p-menuitem-text {
  white-space: nowrap;
}

.p-menu {
  margin: 0 !important;
  white-space: normal;
  word-wrap: break-word;
}

@media (max-width: 835px) {
  .header {
    text-align: center; /* Centers the text horizontally */
    width: 100%; /* Ensures the div takes up the full width */
    margin: 0 auto; /* Centers the div within its parent container */
    padding: 20px 0; /* Adds some vertical padding for spacing */
  }
  .header-container,
  .search-filters {
    display: flex;
    flex-direction: column;
    align-items: center; /* Centers items vertically */
    justify-content: center; /* Helps with horizontal centering if needed */
    width: 100%; /* Ensures full width */
    margin-right: 0; /* Resets any right margin */
  }

  .search-filters {
    order: 2; /* Ensures search filters come right after header */
    margin-top: 20px; /* Adds some space above for visual separation */
  }

  .filters-desktop {
    order: 3; /* Ensures filters are displayed below the search bar */
    flex-wrap: wrap; /* Allows filters to wrap in smaller screens */
    margin-top: 10px; /* Adds space between search bar and filters */
  }

  .fundraiser-cards-container {
    justify-content: center; /* Centers cards horizontally */
    margin-right: 0; /* Removes any right margin that might push content to the left */
    padding: 0 20px; /* Adds padding on the sides */
  }
}

@media (max-width: 768px) {
  .header {
    text-align: center; /* Centers the text horizontally */
    width: 100%; /* Ensures the div takes up the full width */
    margin: 0 auto; /* Centers the div within its parent container */
    padding: 20px 0; /* Adds some vertical padding for spacing */
  }
  .header-container,
  .search-filters,
  .filters-desktop {
    display: flex;
    flex-direction: column;
    align-items: center; /* Centers items vertically */
    justify-content: center; /* Helps with horizontal centering if needed */
    width: 100%; /* Ensures full width */
    margin-right: 0; /* Resets any right margin */
  }

  .search-filters {
    order: 2; /* Ensures search filters come right after header */
    margin-top: 20px; /* Adds some space above for visual separation */
  }

  .filters-desktop {
    order: 3; /* Ensures filters are displayed below the search bar */
    flex-wrap: wrap; /* Allows filters to wrap in smaller screens */
    margin-top: 10px; /* Adds space between search bar and filters */
  }

  .fundraiser-cards-container {
    justify-content: center; /* Centers cards horizontally */
    margin-right: 0; /* Removes any right margin that might push content to the left */
    padding: 0 20px; /* Adds padding on the sides */
  }
}
</style>
